import { type AxiosResponse } from 'axios'
import {
  useQuery,
  type UseQueryResult,
  type UseQueryOptions,
} from 'react-query'

import axiosInstance from '@/lib/axiosInstance'

interface GetUserInfoResponse {
  userName: string
  roles: string[]
  name: string
}

/**
 * Hook: useGetUserInfo
 * ----------------------------
 * Retrieves a paginated list of user bookmarks for a specified chat window type.
 *
 * @param chatWindowType - The type of chat window (CHAT or DOCUMENTATION).
 * @param pageSize - Number of bookmarks per page.
 * @param pageNumber - Page number to retrieve.
 * @param options - Optional query options.
 * @returns A query result containing an array of UserBookmark.
 */
export const useGetUserInfo = (
  options?: UseQueryOptions<GetUserInfoResponse>,
): UseQueryResult<GetUserInfoResponse> => {
  return useQuery<GetUserInfoResponse>(
    ['getUserInfo'],
    async () => getUserInfo(),
    {
      ...options,
    },
  )
}

/**
 * 1. Get User Bookmarks
 * -----------------------
 * Retrieves a paginated list of user bookmarks for a specified chat window type.
 *
 * @param chatWindowType - Type of the chat window (CHAT or DOCUMENTATION).
 * @param pageSize - Number of bookmarks per page (default: 10).
 * @param pageNumber - Page number to retrieve (default: 1).
 * @returns A promise resolving to an array of UserBookmark.
 */
export const getUserInfo = async (): Promise<GetUserInfoResponse> => {
  const url = `/api/v1/auth/user`
  const response: AxiosResponse<GetUserInfoResponse> =
    await axiosInstance.get(url)
  return response.data
}
